import { Component, Property } from '@wonderlandengine/api';
import { vec3 } from 'gl-matrix';
import { AudioSource } from '@wonderlandengine/components';

/**
 * mining-tool.js
 * Standardized mining tool component. Relies on an explicit `muzzle` object
 * (child of the mining-tool or controller) and is driven by external input.
 */
export class MiningTool extends Component {
    static TypeName = 'mining-tool';
    static Properties = {
        muzzle: Property.object(),            // ASSIGN THIS IN EDITOR (Child of Mining Tool)
        laserObjectDesktop: Property.object(),
        laserObjectVR: Property.object(),
        vrMuzzleOrigin: Property.object(),
        vrMuzzleTarget: Property.object(),
        laserMesh: Property.object(),         // The laser mesh to toggle
        mineRate: Property.float(15.0),       // Ticks per second
        miningDamage: Property.float(4.0),
    };

    init() {
        this._isMining = false;
        this._cooldown = 0;
        this._isBuildPreviewing = false;

        // Pre-allocate arrays to prevent GC churn
        this._tempPos = new Float32Array(3);
        this._tempFwd = new Float32Array(3);
        this._tempTarget = new Float32Array(3);
        this._tempRot = new Float32Array(4);
    }

    // Returns {pos, fwd} for the current mode — always rig-correct.
    // VR: right controller world transform (child of playerRig, rotates with rig).
    // Desktop: camera forward (camera is in the rig, always correct after mouse turn).
    _getAim() {
        const isVR = !!(this.engine.xr && this.engine.xr.session);
        const origin = isVR ? this.vrMuzzleOrigin : (this.muzzle || this.object);
        const target = isVR ? this.vrMuzzleTarget : null;

        if (origin && target) {
            const pos = origin.getPositionWorld(this._tempPos);
            const targetPos = target.getPositionWorld(this._tempTarget);
            
            // Calculate direction and speed using gl-matrix
            vec3.subtract(this._tempFwd, targetPos, pos);
            const speed = vec3.length(this._tempFwd);
            if (speed > 0.0001) {
                vec3.scale(this._tempFwd, this._tempFwd, 1.0 / speed);
            }
            return { pos, fwd: this._tempFwd };
        }

        const src = origin || window.VoxelTreadmill?.camera;
        if (src) {
            const pos = src.getPositionWorld(this._tempPos);
            vec3.set(this._tempTarget, 0, 0, -1);
            src.transformPointWorld(this._tempTarget, this._tempTarget);
            vec3.subtract(this._tempFwd, this._tempTarget, pos);
            vec3.normalize(this._tempFwd, this._tempFwd);
            return { pos, fwd: this._tempFwd };
        }
        return null;
    }

    start() {
        if (this.laserObjectDesktop) this.laserObjectDesktop.active = false;
        if (this.laserObjectVR) this.laserObjectVR.active = false;
        this.audio = this.object.getComponent(AudioSource);
    }

    onActivate() {
        if (this.laserObjectDesktop) this.laserObjectDesktop.active = false;
        if (this.laserObjectVR) this.laserObjectVR.active = false;
    }

    onDeactivate() {
        this.stopMining();
        this._isBuildPreviewing = false;
        if (window.VoxelTreadmill) window.VoxelTreadmill._vrPreviewHit = null;
        try { window.OasisMultiplayer?.broadcastLaser(false); } catch (_) {}
    }

    startMining() {
        if (this._isMining) return;
        this._isMining = true;
        const isVR = !!(this.engine.xr && this.engine.xr.session);
        if (this.laserObjectDesktop) this.laserObjectDesktop.active = !isVR;
        if (this.laserObjectVR)      this.laserObjectVR.active      =  isVR;
        const activeLaser = isVR ? this.laserObjectVR : this.laserObjectDesktop;
        if (activeLaser) this.audio?.play();
        try { window.OasisMultiplayer?.broadcastLaser(true); } catch (_) {}
    }

    stopMining() {
        if (!this._isMining) return;
        this._isMining = false;

        if (this.laserObjectDesktop) this.laserObjectDesktop.active = false;
        if (this.laserObjectVR) this.laserObjectVR.active = false;

        this.audio?.stop();
        this._cooldown = 0;
        try { window.OasisMultiplayer?.broadcastLaser(false); } catch (_) {}
    }

    setBuildPreviewing(on) {
        this._isBuildPreviewing = on;
    }

    build() {
        try {
            const vt = window.VoxelTreadmill;
            if (!vt) return;

            const isVR = this.engine.xr && this.engine.xr.session;

            if (isVR) {
                // VR: use controller aim origin so the build matches the controller direction
                const aim = this._getAim();
                if (aim && typeof vt.pullBuildFrom === 'function') {
                    vt.pullBuildFrom(aim.pos, aim.fwd);
                } else if (typeof vt.pullBuild === 'function') {
                    vt.pullBuild();
                }
            } else {
                // Desktop: use camera-center raycast to match the ghost highlight
                if (typeof vt.pullBuild === 'function') {
                    vt.pullBuild();
                }
            }
        } catch (err) {
            console.warn('[MiningTool] build execution error:', err);
        }
    }

    update(dt) {
        const isVR = !!(this.engine.xr && this.engine.xr.session);

        // Always keep laser visibility correct — runs regardless of game state to
        // catch stale active lasers from editor defaults or component re-activation.
        if (this._isMining) {
            if (this.laserObjectDesktop) this.laserObjectDesktop.active = !isVR;
            if (this.laserObjectVR)      this.laserObjectVR.active      =  isVR;
        } else {
            if (this.laserObjectDesktop) this.laserObjectDesktop.active = false;
            if (this.laserObjectVR)      this.laserObjectVR.active      = false;
        }

        if (window.GameState !== 'playing') return;

        const vt = window.VoxelTreadmill;

        if (vt && this._isBuildPreviewing) {
            const aim = this._getAim();
            if (aim) vt._vrPreviewHit = vt._raycastFrom(aim.pos, aim.fwd);
        } else if (vt) {
            vt._vrPreviewHit = null;
        }

        if (this._cooldown > 0) {
            this._cooldown = Math.max(0, this._cooldown - dt);
        }

        if (!this._isMining) return;
        if (this._cooldown > 0) return;

        this._cooldown = 1.0 / Math.max(0.0001, this.mineRate);

        try {
            if (!vt) return;

            // Suppress highlight when mining so it feels like a pure mining beam
            try { vt._suppressNextHighlight = true; } catch (_) {}

            const aim = this._getAim();
            if (aim && typeof vt.pullMineFrom === 'function') {
                vt.pullMineFrom(aim.pos, aim.fwd, this.miningDamage);
            } else if (typeof vt.pullMine === 'function') {
                vt.pullMine(this.miningDamage);
            }
        } catch (err) {
            console.warn('[MiningTool] pullMine execution error:', err);
        }
    }
}